import React, { useState, useRef, useCallback } from 'react';
import Select from 'react-select';
import {
  KTSVG,
  SimpleAlert,
  maskPhoneNumber,
  cleanPhoneNumber,
  toAbsoluteUrl,
} from '../../../../_mexablock/helpers';
import { NumericFormat } from 'react-number-format';
import { Modal } from 'react-bootstrap-v5';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { addDistribuidor } from '../../../service/distribuidores/Distribuidores';
import { useGetOne } from '../hooks/DistribuidorHook';
import {
  useSelectGeneric,
  useSelectTypePerson,
} from '../../catalogos/generico/hooks/genericoHook';

type Props = {
  show: boolean;
  handleClose: (updateGrid: boolean) => void;
  title: string;
  id_row: number;
};

// Teclas permitidas para navegación
const ALLOWED_KEYS = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];

// Validador de teclas para inputs de texto
const createKeyDownHandler = (pattern: RegExp) => (e: React.KeyboardEvent) => {
  if (!pattern.test(e.key) && !ALLOWED_KEYS.includes(e.key)) {
    e.preventDefault();
  }
};

// Patrones de validación
const TEXT_ONLY_PATTERN = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]$/;
const ALPHANUMERIC_PATTERN = /^[a-zA-ZñÑáéíóúÁÉÍÓÚüÜ0-9 ]$/;

// Handlers predefinidos
const handleTextOnlyKeyDown = createKeyDownHandler(TEXT_ONLY_PATTERN);
const handleAlphanumericKeyDown = createKeyDownHandler(ALPHANUMERIC_PATTERN);
const DistribuidorSchema = Yup.object().shape({
  type_contributor_id: Yup.number().required('Requerido.').min(1, 'Requerido.'),
  type_commission_id: Yup.number().required('Requerido.').min(1, 'Requerido.'),
  street: Yup.string()
    .max(30, 'Máximo 30 caracteres')
    .when('type_contributor_id', (type_contributor_id, schema) =>
      type_contributor_id === 2
        ? schema.required('Requerido.')
        : schema.strip(),
    ),
  city: Yup.string()
    .max(50, 'Máximo 50 caracteres')
    .when('type_contributor_id', (type_contributor_id, schema) =>
      type_contributor_id === 2
        ? schema.required('Requerido.')
        : schema.strip(),
    ),
  state: Yup.string()
    .max(30, 'Máximo 50 caracteres')
    .when('type_contributor_id', (type_contributor_id, schema) =>
      type_contributor_id === 2
        ? schema.required('Requerido.')
        : schema.strip(),
    ),
  postal_code: Yup.string()
    .min(5, 'Requerido.')
    .max(5, 'Requerido.')
    .when('type_contributor_id', (type_contributor_id, schema) =>
      type_contributor_id === 2
        ? schema.required('Requerido.')
        : schema.strip(),
    ),
  name: Yup.string()
    .max(100, 'Máximo 100 caracteres')
    .when('type_contributor_id', (type_contributor_id, schema) =>
      type_contributor_id === 2
        ? schema.required('Requerido.')
        : schema.strip(),
    ),
  business_name: Yup.string()
    .max(100, 'Máximo 100 caracteres')
    .when('type_contributor_id', (type_contributor_id, schema) =>
      type_contributor_id === 2
        ? schema.required('Requerido.')
        : schema.strip(),
    ),
  email: Yup.string()
    .email('Formato de correo inválido.')
    .min(3, 'Mínimo 3 caracteres.')
    .max(50, 'Máximo 50 caracteres.')
    .required('Requerido.'),
  billing_mail: Yup.string()
    .email('Formato de correo inválido.')
    .min(3, 'Mínimo 3 caracteres.')
    .max(50, 'Máximo 50 caracteres.')
    .required('Requerido.'),

  cfdi_id: Yup.number().required('Requerido.').min(1, 'Requerido.'),
  pdfFile: Yup.mixed()
    .required('Requerido.')
    .test('is-pdf', 'El archivo debe ser un PDF', (value) => {
      if (value) {
        return value.type === 'application/pdf';
      }
      return false;
    }),
  rfc: Yup.string()
    .required('Requerido.')
    .matches(
      /^([A-ZÑ&]{3,4}) ?(?:- ?)?(\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])) ?(?:- ?)?([A-Z\d]{2})([A\d])$/,
      'Formato de RFC inválido',
    ),
  interbank_key: Yup.string()
    .min(18, 'Mínimo 18 caracteres')
    .required('Requerido.'),
  first_name: Yup.string()
    .max(30, 'Máximo 30 caracteres')
    .required('Requerido.'),
  mother_last_name: Yup.string()
    .max(30, 'Máximo 30 caracteres')
    .required('Requerido.'),
  father_last_name: Yup.string()
    .max(30, 'Máximo 30 caracteres')
    .required('Requerido.'),
  birthdate: Yup.date().required('Requerido.'),
  phone: Yup.string().required('Requerido.').min(10, 'Formato inválido'),
  gender_id: Yup.number().required('Requerido.').min(1, 'Requerido.'),
  account_owner_first_name: Yup.string()
    .max(30, 'Máximo 30 caracteres')
    .required('Requerido.'),
  account_owner_last_name: Yup.string()
    .max(30, 'Máximo 30 caracteres')
    .required('Requerido.'),
  bank_name: Yup.string()
    .max(30, 'Máximo 30 caracteres')
    .required('Requerido.'),
  commission_percent: Yup.number()
    .max(100, 'El porcentaje no puede ser mayor que 100')
    .min(0, 'El porcentaje no puede ser menor que 0')
    .required('Requerido.'),
});

const currentDate = new Date();
const fechaMaxima = new Date(currentDate);
fechaMaxima.setFullYear(currentDate.getFullYear() - 18); // Restar 18 años
const maxDate = fechaMaxima.toISOString().split('T')[0];

const CreateModal: React.FC<Props> = ({ show, handleClose }) => {
  const [loading, setLoading] = useState(false);
  const { initValues } = useGetOne();
  const constancyRef = useRef<HTMLInputElement>(null);
  const [documentName, setDocumentName] = useState<string>('');
  const { catalog: genders } = useSelectGeneric('GEN');
  const { catalog: cfdi } = useSelectGeneric('CFDI');
  const { catalog: tipo_comision } = useSelectGeneric('TDC');
  const { catalogTypePerson: tipo_persona } = useSelectTypePerson();
  const [changeTypeContributor, setChangeTypeContributor] = useState<number>(2);

  const resetModal = useCallback(() => {
    setDocumentName('');
    setChangeTypeContributor(2);
  }, []);

  const formDistribuidor = useFormik({
    initialValues: { ...initValues, type_contributor_id: 2 },
    validationSchema: DistribuidorSchema,
    enableReinitialize: true,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      addDistribuidor(values)
        .then((res) => {
          const {
            data: { message },
          } = res;
          SimpleAlert(message, 3000, 'success');
          formDistribuidor.resetForm();
          resetModal();
          setLoading(false);
          handleClose(true);
        })
        .catch((err) => {
          const {
            data: { message, details = null },
          } = err.response;
          if (details?.length) {
            setStatus(`${details[0]?.message}`);
          }
          setLoading(false);
          setSubmitting(false);
          SimpleAlert(message, 3000, 'error');
        });
    },
  });

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const maskedPhoneNumber = maskPhoneNumber(e.target.value);
      formDistribuidor.setFieldValue(
        'phone',
        cleanPhoneNumber(maskedPhoneNumber),
      );
    },
    [formDistribuidor],
  );

  const onOpenFile = useCallback(() => {
    constancyRef.current?.click();
  }, []);

  const handleChangeInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
      const cleanInput = e.target.value
        .replace(/\d/g, '')
        .replace(/^\s+|\s+(?=\s)/g, '')
        .replace(/[^\wáéíóúüÁÉÍÓÚÜñÑ\s]/g, '');
      formDistribuidor.setFieldValue(key, cleanInput);
    },
    [formDistribuidor],
  );

  const handleChangeInputNumeric = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
      const cleanInput = e.target.value.replace(/[^\w\dáéíóúüÁÉÍÓÚÜñÑ]/g, '');
      formDistribuidor.setFieldValue(key, cleanInput);
    },
    [formDistribuidor],
  );

  const changeUtilityValue = useCallback(
    (values: any) => {
      formDistribuidor.setFieldValue(
        'commission_percent',
        values.floatValue || 0,
      );
    },
    [formDistribuidor],
  );

  const handleModalClose = useCallback(() => {
    resetModal();
    formDistribuidor.resetForm();
    handleClose(false);
  }, [resetModal, formDistribuidor, handleClose]);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.currentTarget.files;
      if (files && files.length > 0) {
        formDistribuidor.setFieldValue('pdfFile', files[0]);
        setDocumentName(files[0].name);
      }
    },
    [formDistribuidor],
  );

  const isAllowedPercent = useCallback((values: any) => {
    const { floatValue } = values;
    return floatValue <= 100 || floatValue === undefined;
  }, []);

  const handleTypeContributorChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = Number(e.target.value);
      formDistribuidor.setFieldValue('name', '');
      formDistribuidor.setFieldValue('business_name', '');
      formDistribuidor.setFieldValue('commission_percent', '');
      formDistribuidor.setFieldValue('street', '');
      formDistribuidor.setFieldValue('city', '');
      formDistribuidor.setFieldValue('state', '');
      formDistribuidor.setFieldValue('type_commission_id', 0);
      formDistribuidor.setFieldValue('postal_code', 0);
      formDistribuidor.setFieldValue('type_contributor_id', value);
      setChangeTypeContributor(value);
      formDistribuidor.setErrors({});
      formDistribuidor.setTouched({});
    },
    [formDistribuidor],
  );

  return (
    <Modal
      id="kt_modal_create"
      tabIndex={-1}
      aria-hidden="true"
      dialogClassName="modal-dialog modal-dialog-centered modal-xl"
      show={show}
      onHide={handleModalClose}
    >
      <div className="modal-content">
        <div className="modal-header pb-0 border-0 justify-content-end">
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            data-bs-dismiss="modal"
            onClick={handleModalClose}
          >
            <KTSVG
              className="svg-icon-2"
              path="/media/icons/duotune/arrows/arr061.svg"
            />
          </div>
        </div>

        <div className="modal-body scroll-y mx-5 pt-0">
          <div className="text-center mb-13">
            <h1 className="mb-3">Nuevo distribuidor</h1>
            <div className="text-muted fw-semibold fs-5">
              Agrega la información del distribuidor
            </div>
          </div>
          <form
            id="kt_modal_generic_form"
            className="form"
            action="#"
            onSubmit={formDistribuidor.handleSubmit}
          >
            {/* Sección: Datos de identificación */}
            <div className="text-gray-600 fs-5 fw-semibold mb-5">
              Datos de identificación
            </div>

            {/* Fila 1: Régimen + campos condicionales */}
            <div className="row g-9 mb-5">
              <div className="col-md-3">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Regimen
                  </label>
                </div>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={tipo_persona.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                  value={
                    tipo_persona
                      .map((item) => ({ value: item.id, label: item.name }))
                      .find(
                        (op) =>
                          op.value === formDistribuidor.values.type_contributor_id,
                      ) || null
                  }
                  onChange={(option) => {
                    handleTypeContributorChange({
                      target: { value: option?.value || 0 },
                    } as any);
                  }}
                  placeholder="Seleccione"
                />
                {formDistribuidor.touched.type_contributor_id &&
                  formDistribuidor.errors.type_contributor_id && (
                    <div className="fv-plugins-message-container text-danger w-auto">
                      <span role="alert">
                        {formDistribuidor.errors.type_contributor_id}
                      </span>
                    </div>
                  )}
              </div>

              {/* Física: Comisión, % Comisión */}
              {changeTypeContributor === 1 && (
                <>
                  <div className="col-md-3">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2 w-auto">
                        Comisión
                      </label>
                    </div>
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      options={tipo_comision.map((item) => ({
                        value: item.id,
                        label: item.name,
                      }))}
                      value={
                        tipo_comision
                          .map((item) => ({ value: item.id, label: item.name }))
                          .find(
                            (op) =>
                              op.value ===
                              formDistribuidor.values.type_commission_id,
                          ) || null
                      }
                      onChange={(option) => {
                        formDistribuidor.setFieldValue(
                          'type_commission_id',
                          option?.value || 0,
                        );
                      }}
                      onBlur={() => {
                        formDistribuidor.setFieldTouched(
                          'type_commission_id',
                          true,
                        );
                      }}
                      placeholder="Seleccione"
                    />
                    {formDistribuidor.touched.type_commission_id &&
                      formDistribuidor.errors.type_commission_id && (
                        <div className="fv-plugins-message-container text-danger w-auto">
                          <span role="alert">
                            {formDistribuidor.errors.type_commission_id}
                          </span>
                        </div>
                      )}
                  </div>
                  <div className="col-md-3 fv-row fv-plugins-icon-container">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2">
                        % Comisión
                      </label>
                    </div>
                    <NumericFormat
                      type="text"
                      name="commission_percent"
                      className="form-control"
                      value={formDistribuidor.values.commission_percent}
                      decimalScale={0}
                      allowLeadingZeros
                      fixedDecimalScale
                      placeholder="0"
                      suffix={' %'}
                      thousandSeparator={''}
                      allowNegative={false}
                      isAllowed={isAllowedPercent}
                      onValueChange={changeUtilityValue}
                    />
                    {formDistribuidor.touched.commission_percent &&
                      formDistribuidor.errors.commission_percent && (
                        <div className="fv-plugins-message-container text-danger">
                          <span role="alert">
                            {formDistribuidor.errors.commission_percent}
                          </span>
                        </div>
                      )}
                  </div>
                </>
              )}

              {/* Moral: Nombre empresa, Razón social */}
              {changeTypeContributor === 2 && (
                <>
                  <div className="col-md-3 fv-row fv-plugins-icon-container">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2 w-auto">
                        Nombre de la empresa
                      </label>
                    </div>
                    <input
                      placeholder="Nombre de la empresa"
                      {...formDistribuidor.getFieldProps('name')}
                      className="form-control"
                      type="text"
                      name="name"
                      autoComplete="off"
                      onKeyDown={handleTextOnlyKeyDown}
                    />
                    {formDistribuidor.touched.name &&
                      formDistribuidor.errors.name && (
                        <div className="fv-plugins-message-container text-danger">
                          <span role="alert">
                            {formDistribuidor.errors.name}
                          </span>
                        </div>
                      )}
                  </div>
                  <div className="col-md-3">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2 w-auto">
                        Razón social
                      </label>
                    </div>
                    <input
                      placeholder="Razón social"
                      {...formDistribuidor.getFieldProps('business_name')}
                      className="form-control"
                      type="text"
                      name="business_name"
                      autoComplete="off"
                      onKeyDown={handleTextOnlyKeyDown}
                    />
                    {formDistribuidor.touched.business_name &&
                      formDistribuidor.errors.business_name && (
                        <div className="fv-plugins-message-container text-danger">
                          <span role="alert">
                            {formDistribuidor.errors.business_name}
                          </span>
                        </div>
                      )}
                  </div>
                </>
              )}
            </div>

            {/* Moral: Fila 2 - Comisión, % Comisión */}
            {changeTypeContributor === 2 && (
              <>
                <div className="row g-9 mb-5">
                  <div className="col-md-3">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2 w-auto">
                        Comisión
                      </label>
                    </div>
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      options={tipo_comision.map((item) => ({
                        value: item.id,
                        label: item.name,
                      }))}
                      value={
                        tipo_comision
                          .map((item) => ({ value: item.id, label: item.name }))
                          .find(
                            (op) =>
                              op.value ===
                              formDistribuidor.values.type_commission_id,
                          ) || null
                      }
                      onChange={(option) => {
                        formDistribuidor.setFieldValue(
                          'type_commission_id',
                          option?.value || 0,
                        );
                      }}
                      onBlur={() => {
                        formDistribuidor.setFieldTouched(
                          'type_commission_id',
                          true,
                        );
                      }}
                      placeholder="Seleccione"
                    />
                    {formDistribuidor.touched.type_commission_id &&
                      formDistribuidor.errors.type_commission_id && (
                        <div className="fv-plugins-message-container text-danger">
                          <span role="alert">
                            {formDistribuidor.errors.type_commission_id}
                          </span>
                        </div>
                      )}
                  </div>
                  <div className="col-md-3 fv-row fv-plugins-icon-container">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2">
                        % Comisión
                      </label>
                    </div>
                    <NumericFormat
                      type="text"
                      name="commission_percent"
                      className="form-control"
                      value={formDistribuidor.values.commission_percent}
                      decimalScale={0}
                      allowLeadingZeros
                      fixedDecimalScale
                      placeholder="0"
                      suffix={' %'}
                      thousandSeparator={''}
                      allowNegative={false}
                      isAllowed={isAllowedPercent}
                      onValueChange={changeUtilityValue}
                    />
                    {formDistribuidor.touched.commission_percent &&
                      formDistribuidor.errors.commission_percent && (
                        <div className="fv-plugins-message-container text-danger">
                          <span role="alert">
                            {formDistribuidor.errors.commission_percent}
                          </span>
                        </div>
                      )}
                  </div>
                </div>

                {/* Sección: Ubicación de la empresa (solo Moral) */}
                <div className="text-gray-600 fs-5 fw-semibold mb-5">
                  Ubicación de la empresa
                </div>
                <div className="separator mb-5"></div>
                <div className="row g-9 mb-5">
                  <div className="col-md-3 fv-row fv-plugins-icon-container">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2">
                        Código postal
                      </label>
                    </div>
                    <NumericFormat
                      name="postal_code"
                      className="form-control"
                      value={formDistribuidor.values.postal_code}
                      decimalScale={0}
                      allowLeadingZeros
                      maxLength={5}
                      fixedDecimalScale
                      placeholder="0"
                      thousandSeparator=""
                      onValueChange={(values) => {
                        formDistribuidor.setFieldValue(
                          'postal_code',
                          values.value || '',
                        );
                      }}
                    />
                    {formDistribuidor.touched.postal_code &&
                      formDistribuidor.errors.postal_code && (
                        <div className="fv-plugins-message-container text-danger">
                          <span role="alert">
                            {formDistribuidor.errors.postal_code}
                          </span>
                        </div>
                      )}
                  </div>
                  <div className="col-md-3">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2 w-auto">
                        Estado
                      </label>
                    </div>
                    <input
                      placeholder="Estado"
                      {...formDistribuidor.getFieldProps('state')}
                      className="form-control"
                      type="text"
                      name="state"
                      autoComplete="off"
                      onKeyDown={handleTextOnlyKeyDown}
                    />
                    {formDistribuidor.touched.state &&
                      formDistribuidor.errors.state && (
                        <div className="fv-plugins-message-container text-danger">
                          <span role="alert">
                            {formDistribuidor.errors.state}
                          </span>
                        </div>
                      )}
                  </div>
                  <div className="col-md-3">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2 w-auto">
                        Ciudad
                      </label>
                    </div>
                    <input
                      placeholder="Ciudad"
                      {...formDistribuidor.getFieldProps('city')}
                      className="form-control"
                      type="text"
                      name="city"
                      autoComplete="off"
                      onKeyDown={handleTextOnlyKeyDown}
                    />
                    {formDistribuidor.touched.city &&
                      formDistribuidor.errors.city && (
                        <div className="fv-plugins-message-container text-danger">
                          <span role="alert">
                            {formDistribuidor.errors.city}
                          </span>
                        </div>
                      )}
                  </div>
                  <div className="col-md-3">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2 w-auto">
                        Calle y Número
                      </label>
                    </div>
                    <input
                      placeholder="ej. Calle Marfil #149"
                      {...formDistribuidor.getFieldProps('street')}
                      className="form-control"
                      type="text"
                      name="street"
                      autoComplete="off"
                      onKeyDown={handleAlphanumericKeyDown}
                    />
                    {formDistribuidor.touched.street &&
                      formDistribuidor.errors.street && (
                        <div className="fv-plugins-message-container text-danger">
                          <span role="alert">
                            {formDistribuidor.errors.street}
                          </span>
                        </div>
                      )}
                  </div>
                </div>
              </>
            )}
            {/* Sección: Datos del encargado */}
            <div className="text-gray-600 fs-5 fw-semibold mb-5">
              Datos del encargado
            </div>
            <div className="separator mb-5"></div>
            <div className="row g-9 mb-5">
              <div className="col-md-4 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Nombre
                  </label>
                </div>
                <input
                  placeholder="Nombre del encargado"
                  {...formDistribuidor.getFieldProps('first_name')}
                  className="form-control mb-3 mb-lg-0"
                  type="text"
                  name="first_name"
                  autoComplete="off"
                  onChange={(e) => handleChangeInput(e, 'first_name')}
                  onKeyDown={handleTextOnlyKeyDown}
                />
                {formDistribuidor.touched.first_name &&
                  formDistribuidor.errors.first_name && (
                    <div className="fv-plugins-message-container text-danger w-100 ms-5">
                      <span role="alert">
                        {formDistribuidor.errors.first_name}
                      </span>
                    </div>
                  )}
              </div>
              <div className="col-md-4 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-40">
                    Apellido Paterno
                  </label>
                </div>
                <input
                  placeholder="Apellido Paterno"
                  {...formDistribuidor.getFieldProps('father_last_name')}
                  className="form-control mb-3 mb-lg-0"
                  type="text"
                  name="user.father_last_name"
                  autoComplete="off"
                  onChange={(e) => handleChangeInput(e, 'father_last_name')}
                  onKeyDown={handleTextOnlyKeyDown}
                />
                {formDistribuidor.touched.father_last_name &&
                  formDistribuidor.errors.father_last_name && (
                    <div className="fv-plugins-message-container text-danger w-50 ms-5">
                      <span role="alert">
                        {formDistribuidor.errors.father_last_name}
                      </span>
                    </div>
                  )}
              </div>
              <div className="col-md-4 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="fw-bold fs-6 mb-2 w-40">
                    Apellido Materno
                  </label>
                </div>
                <input
                  placeholder="Apellido Materno"
                  {...formDistribuidor.getFieldProps('mother_last_name')}
                  className="form-control mb-3 mb-lg-0"
                  type="text"
                  name="mother_last_name"
                  autoComplete="off"
                  onChange={(e) => handleChangeInput(e, 'mother_last_name')}
                  onKeyDown={handleTextOnlyKeyDown}
                />
                {formDistribuidor.touched.mother_last_name &&
                  formDistribuidor.errors.mother_last_name && (
                    <div className="fv-plugins-message-container text-danger w-60 ms-5">
                      <span role="alert">
                        {formDistribuidor.errors.mother_last_name}
                      </span>
                    </div>
                  )}
              </div>
            </div>
            <div className="row g-9 mb-5">
              <div className="col-md-3 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Género
                  </label>
                </div>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={genders.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                  value={
                    genders
                      .map((item) => ({ value: item.id, label: item.name }))
                      .find((op) => op.value === formDistribuidor.values.gender_id) ||
                    null
                  }
                  onChange={(option) => {
                    formDistribuidor.setFieldValue('gender_id', option?.value || 0);
                  }}
                  onBlur={() => {
                    formDistribuidor.setFieldTouched('gender_id', true);
                  }}
                  placeholder="Seleccione"
                />
                {formDistribuidor.touched.gender_id &&
                  formDistribuidor.errors.gender_id && (
                    <div className="fv-plugins-message-container text-danger w-auto ms-5">
                      <span role="alert">
                        {formDistribuidor.errors.gender_id}
                      </span>
                    </div>
                  )}
              </div>
              <div className="col-md-3 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Fecha de nacimiento
                  </label>
                </div>
                <input
                  type="date"
                  className="form-control"
                  value={formDistribuidor.values.birthdate}
                  max={maxDate}
                  onChange={(e) => {
                    if (e.target.value) {
                      const fecha = new Date(e.target.value)
                        .toISOString()
                        .split('T')[0];
                      formDistribuidor.setFieldValue('birthdate', fecha);
                    }
                  }}
                ></input>
                {formDistribuidor.touched.birthdate &&
                  formDistribuidor.errors.birthdate && (
                    <div className="fv-plugins-message-container text-danger w-auto ms-5">
                      <span role="alert">
                        {formDistribuidor.errors.birthdate}
                      </span>
                    </div>
                  )}
              </div>
              <div className="col-md-3 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Correo
                  </label>
                </div>
                <input
                  placeholder="Correo"
                  {...formDistribuidor.getFieldProps('email')}
                  className="form-control mb-3 mb-lg-0"
                  type="text"
                  name="email"
                  autoComplete="off"
                />
                {formDistribuidor.touched.email &&
                  formDistribuidor.errors.email && (
                    <div className="fv-plugins-message-container text-danger w-auto ms-5">
                      <span role="alert">{formDistribuidor.errors.email}</span>
                    </div>
                  )}
              </div>
              <div className="col-md-3 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Teléfono
                  </label>
                </div>
                <input
                  className="form-control mb-3"
                  value={formDistribuidor.values.phone}
                  placeholder="Teléfono"
                  prefix=""
                  onChange={handlePhoneChange}
                />
                {formDistribuidor.touched.phone &&
                  formDistribuidor.errors.phone && (
                    <div className="fv-plugins-message-container text-danger w-auto ms-5">
                      <span role="alert">{formDistribuidor.errors.phone}</span>
                    </div>
                  )}
              </div>
            </div>

            {/* Sección: Datos bancarios */}
            <div className="text-gray-600 fs-5 fw-semibold mb-5">
              Datos bancarios
            </div>
            <div className="separator mb-5"></div>
            <div className="row g-9 mb-5">
              <div className="col-md-3 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Nombre
                  </label>
                </div>
                <input
                  placeholder="Nombre"
                  {...formDistribuidor.getFieldProps('account_owner_first_name')}
                  className="form-control"
                  type="text"
                  name="account_owner_first_name"
                  autoComplete="off"
                  onChange={(e) => handleChangeInput(e, 'account_owner_first_name')}
                  onKeyDown={handleTextOnlyKeyDown}
                />
                {formDistribuidor.touched.account_owner_first_name &&
                  formDistribuidor.errors.account_owner_first_name && (
                    <div className="fv-plugins-message-container text-danger">
                      <span role="alert">
                        {formDistribuidor.errors.account_owner_first_name}
                      </span>
                    </div>
                  )}
              </div>
              <div className="col-md-3 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2">
                    Apellido Paterno
                  </label>
                </div>
                <input
                  placeholder="Apellido Paterno"
                  {...formDistribuidor.getFieldProps('account_owner_last_name')}
                  className="form-control"
                  type="text"
                  name="account_owner_last_name"
                  autoComplete="off"
                  onChange={(e) => handleChangeInput(e, 'account_owner_last_name')}
                  onKeyDown={handleTextOnlyKeyDown}
                />
                {formDistribuidor.touched.account_owner_last_name &&
                  formDistribuidor.errors.account_owner_last_name && (
                    <div className="fv-plugins-message-container text-danger">
                      <span role="alert">
                        {formDistribuidor.errors.account_owner_last_name}
                      </span>
                    </div>
                  )}
              </div>
              <div className="col-md-3 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2">
                    Banco
                  </label>
                </div>
                <input
                  placeholder="ej. Banco azteca"
                  {...formDistribuidor.getFieldProps('bank_name')}
                  className="form-control"
                  type="text"
                  name="bank_name"
                  autoComplete="off"
                  onChange={(e) => handleChangeInput(e, 'bank_name')}
                  onKeyDown={handleTextOnlyKeyDown}
                />
                {formDistribuidor.touched.bank_name &&
                  formDistribuidor.errors.bank_name && (
                    <div className="fv-plugins-message-container text-danger">
                      <span role="alert">
                        {formDistribuidor.errors.bank_name}
                      </span>
                    </div>
                  )}
              </div>
              <div className="col-md-3 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2">
                    CLABE Interbancaria
                  </label>
                </div>
                <NumericFormat
                  name="clabe"
                  className="form-control"
                  value={formDistribuidor.values.interbank_key}
                  decimalScale={0}
                  allowLeadingZeros
                  maxLength={18}
                  fixedDecimalScale
                  placeholder="CLABE interbancaria"
                  thousandSeparator=""
                  onValueChange={(values) => {
                    formDistribuidor.setFieldValue(
                      'interbank_key',
                      values.value || '',
                    );
                  }}
                />
                {formDistribuidor.touched.interbank_key &&
                  formDistribuidor.errors.interbank_key && (
                    <div className="fv-plugins-message-container text-danger">
                      <span role="alert">
                        {formDistribuidor.errors.interbank_key}
                      </span>
                    </div>
                  )}
              </div>
            </div>

            {/* Sección: Datos de facturación */}
            <div className="text-gray-600 fs-5 fw-semibold mb-5">
              Datos de facturación
            </div>
            <div className="separator mb-5"></div>
            <div className="row g-9 mb-5">
              <div className="col-md-4 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Uso de CFDI
                  </label>
                </div>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={cfdi.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                  value={
                    cfdi
                      .map((item) => ({ value: item.id, label: item.name }))
                      .find((op) => op.value === formDistribuidor.values.cfdi_id) ||
                    null
                  }
                  onChange={(option) => {
                    formDistribuidor.setFieldValue('cfdi_id', option?.value || 0);
                  }}
                  onBlur={() => {
                    formDistribuidor.setFieldTouched('cfdi_id', true);
                  }}
                  placeholder="Seleccione"
                />
                {formDistribuidor.touched.cfdi_id &&
                  formDistribuidor.errors.cfdi_id && (
                    <div className="fv-plugins-message-container text-danger w-auto ms-5">
                      <span role="alert">
                        {formDistribuidor.errors.cfdi_id}
                      </span>
                    </div>
                  )}
              </div>
              <div className="col-md-4 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-40">
                    Correo
                  </label>
                </div>
                <input
                  placeholder="Correo"
                  {...formDistribuidor.getFieldProps('billing_mail')}
                  className="form-control mb-3 mb-lg-0"
                  type="text"
                  name="billing_mail"
                  autoComplete="off"
                />
                {formDistribuidor.touched.billing_mail &&
                  formDistribuidor.errors.billing_mail && (
                    <div className="fv-plugins-message-container text-danger w-60 ms-5">
                      <span role="alert">
                        {formDistribuidor.errors.billing_mail}
                      </span>
                    </div>
                  )}
              </div>
              <div className="col-md-4 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className=" fw-bold fs-6 mb-2 w-auto required">
                    RFC
                  </label>
                </div>
                <input
                  placeholder="RFC"
                  {...formDistribuidor.getFieldProps('rfc')}
                  className="form-control mb-3 mb-lg-0"
                  maxLength={13}
                  type="text"
                  name="rfc"
                  autoComplete="off"
                  onChange={(e) => handleChangeInputNumeric(e, 'rfc')}
                />
                {formDistribuidor.touched.rfc && formDistribuidor.errors.rfc && (
                  <div className="fv-plugins-message-container text-danger w-auto ms-5">
                    <span role="alert">{formDistribuidor.errors.rfc}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="row g-9 justify-content-center text-center">
              <div className="col-md-6 fv-row fv-plugins-icon-container">
                <div className="d-flex justify-content-center text-center p-2">
                  <a
                    href="#/"
                    className="text-gray-800 text-hover-primary d-flex flex-column"
                  >
                    <div className="symbol symbol-50px mb-6">
                      <img
                        src={
                          formDistribuidor.values.pdfFile
                            ? toAbsoluteUrl('/media/svg/files/pdf.svg')
                            : toAbsoluteUrl('/media/svg/files/upload.svg')
                        }
                        className="image-input-wrapper"
                        onClick={onOpenFile}
                        crossOrigin="anonymous"
                        alt="INE"
                      />
                    </div>

                    <div className="d-flex">
                      <div
                        className="required fs-5 mb-2 flex-column"
                        onClick={onOpenFile}
                      >
                        Constancia de situación fiscal
                      </div>
                      <div className="fs-5 mb-2 flex-column ms-5">
                        {formDistribuidor.touched.pdfFile &&
                          formDistribuidor.errors.pdfFile && (
                            <div className="fv-plugins-message-container text-danger w-100">
                              <span role="alert">
                                {formDistribuidor.errors.pdfFile}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                    <label className="min-h-30px text-success">
                      {documentName}
                    </label>

                    <input
                      style={{ display: 'none' }}
                      ref={constancyRef}
                      type="file"
                      name="constancy"
                      accept=".pdf"
                      onChange={handleFileChange}
                    />
                  </a>
                </div>
              </div>
            </div>

            <div className="text-center pt-10">
              <button
                type="reset"
                className="btn btn-light me-3"
                data-kt-users-modal-action="cancel"
                onClick={handleModalClose}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                data-kt-users-modal-action="submit"
                disabled={loading}
              >
                {!loading && <span className="indicator-label">Guardar</span>}
                {loading && (
                  <span
                    className="indicator-progress"
                    style={{ display: 'block' }}
                  >
                    Espere por favor...
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};
export { CreateModal };
